import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import PageHeader from '../../../components/PageHeader';
import Lightbox from '../../../components/Lightbox';
import { 
  getAvailableYears, 
  getYearStats, 
  getPhotosForYear,
  getAlbumsForYear,
  getAlbumById,
  getPhotosForAlbum,
  getAlbumStats
} from './data';
import './PhotoGallery.css';
import usePageSearchIndex from '../../../hooks/usePageSearchIndex';

const PhotoGallery = () => {
  const { isMarathi } = useLanguage();
  const { year, albumId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Determine current view
  const showingYearList = !year;
  const selectedYear = year ? parseInt(year) : null;
  const selectedAlbumId = albumId ? parseInt(albumId) : null;

  // Get data based on current view
  const availableYears = getAvailableYears();
  const yearAlbums = selectedYear ? getAlbumsForYear(selectedYear) : [];
  const albumPhotos = (selectedYear && selectedAlbumId) 
    ? getPhotosForAlbum(selectedYear, selectedAlbumId) 
    : [];
  const yearPhotos = selectedYear && !selectedAlbumId 
    ? getPhotosForYear(selectedYear) 
    : [];

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Auto-index Photo Gallery page content for search
  usePageSearchIndex({
    mr: ['फोटो', 'गॅलरी', 'छायाचित्रे', 'वर्ष'],
    en: ['photo', 'gallery', 'photos', 'images', 'year']
  });

  // Auto-redirect to latest year when no year is selected
  useEffect(() => {
    if (showingYearList && availableYears.length > 0) {
      const latestYear = availableYears[0]; // Already sorted with latest first
      navigate(`/photo-gallery/${latestYear}`, { replace: true });
    }
  }, [showingYearList, availableYears, navigate]);

  const pageHeaderData = {
    title: {
      marathi: showingYearList 
        ? 'फोटो गॅलरी' 
        : selectedAlbumId 
          ? `फोटो गॅलरी - ${selectedYear}` 
          : `फोटो गॅलरी - ${selectedYear}`,
      english: showingYearList 
        ? 'Photo Gallery' 
        : selectedAlbumId 
          ? `Photo Gallery - ${selectedYear}` 
          : `Photo Gallery - ${selectedYear}`
    },
    subtitle: {
      marathi: showingYearList 
        ? 'वर्षानुसार छायाचित्रे पहा' 
        : selectedAlbumId
          ? `${getAlbumById(selectedYear, selectedAlbumId)?.title?.marathi || ''}`
          : `${selectedYear} च्या कार्यक्रमांची छायाचित्रे`,
      english: showingYearList 
        ? 'View photos by year' 
        : selectedAlbumId
          ? `${getAlbumById(selectedYear, selectedAlbumId)?.title?.english || ''}`
          : `Photos from ${selectedYear} events`
    }
  };

  const handleYearClick = (year) => {
    navigate(`/photo-gallery/${year}`);
  };

  const handleAlbumClick = (albumId) => {
    navigate(`/photo-gallery/${selectedYear}/${albumId}`);
  };

  const handleBackToAlbums = () => {
    navigate(`/photo-gallery/${selectedYear}`);
  };

  const handlePhotoClick = (photoIndex) => {
    setCurrentPhotoIndex(photoIndex);
    setIsLightboxOpen(true);
  };

  const handleLightboxClose = () => {
    setIsLightboxOpen(false);
  };

  const handlePreviousPhoto = () => {
    const photos = selectedAlbumId ? albumPhotos : yearPhotos;
    setCurrentPhotoIndex((prev) => 
      prev === 0 ? photos.length - 1 : prev - 1
    );
  };

  const handleNextPhoto = () => {
    const photos = selectedAlbumId ? albumPhotos : yearPhotos;
    setCurrentPhotoIndex((prev) => 
      prev === photos.length - 1 ? 0 : prev + 1
    );
  };

  // Determine which photos to show in lightbox
  const lightboxPhotos = selectedAlbumId ? albumPhotos : yearPhotos;

  if (isLoading) {
    return (
      <div className="photo-gallery">
        <div className="photo-gallery-container">
          <PageHeader 
            title={pageHeaderData.title}
            subtitle={pageHeaderData.subtitle}
          />
          <div className="gallery-loading">
            <div className="loading-spinner"></div>
            <p className="loading-text">
              {isMarathi() ? 'लोड होत आहे...' : 'Loading...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="photo-gallery">
      <div className="photo-gallery-container">
        <PageHeader 
          title={pageHeaderData.title}
          subtitle={pageHeaderData.subtitle}
        />

        <div className="photo-gallery-layout">
          {/* Year Timeline Sidebar */}
          <div className="year-filter-sidebar">
            <h3 className="year-filter-title">
              {isMarathi() ? 'वर्ष' : 'Year'}
            </h3>
            <div className="year-filter-list">
              {availableYears.map((year) => {
                const yearStats = getYearStats(year);
                return (
                  <button
                    key={year}
                    className={`year-filter-item ${selectedYear === year ? 'active' : ''}`}
                    onClick={() => handleYearClick(year)}
                  >
                    {year}
                    <span className="year-photo-count">
                      ({yearStats.albumCount} {isMarathi() ? 'अल्बम' : 'albums'})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Content */}
          <div className="photo-gallery-main">
            {showingYearList ? (
              /* Welcome Message */
              <div className="gallery-welcome">
                <p className="gallery-welcome-text">
                  {isMarathi() 
                    ? 'डावीकडील वर्ष निवडा छायाचित्रे पाहण्यासाठी' 
                    : 'Select a year from the left to view photos'}
                </p>
              </div>
            ) : selectedAlbumId ? (
              /* Album Photos View */
              <div className="album-photos-container">
                {/* Back Button */}
                <div className="album-photos-header">
                  <button 
                    onClick={handleBackToAlbums}
                    className="back-to-albums-btn"
                  >
                    ← {isMarathi() ? 'अल्बमकडे परत जा' : 'Back to Albums'}
                  </button>
                  <div className="album-photos-stats">
                    <p className="total-photos">
                      {isMarathi() 
                        ? `एकूण छायाचित्रे: ${albumPhotos.length}` 
                        : `Total Photos: ${albumPhotos.length}`
                      }
                    </p>
                  </div>
                </div>

                {/* Photos Grid */}
                <div className="year-photos-grid">
                  {albumPhotos.length > 0 ? (
                    albumPhotos.map((photo, index) => (
                      <div
                        key={photo.id}
                        className="year-photo-item"
                        onClick={() => handlePhotoClick(index)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePhotoClick(index);
                          }
                        }}
                        aria-label={`${isMarathi() ? 'छायाचित्र उघडा' : 'Open photo'}: ${isMarathi() ? photo.title?.marathi || '' : photo.title?.english || ''}`}
                      >
                        <picture>
                          {photo.image && <source srcSet={photo.image} type="image/webp" />}
                          <img
                            src={photo.fallback || photo.url}
                            alt={isMarathi() ? (photo.title?.marathi || '') : (photo.title?.english || '')}
                            className="year-photo-image"
                            loading="lazy"
                            onError={(e) => {
                              console.error('Image failed to load:', photo.fallback || photo.url);
                            }}
                            onLoad={() => {
                              console.log('Image loaded successfully:', photo.fallback || photo.url);
                            }}
                          />
                        </picture>
                        <div className="year-photo-overlay">
                          <h4 className="year-photo-title">
                            {isMarathi() ? (photo.title?.marathi || '') : (photo.title?.english || '')}
                          </h4>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-photos-message">
                      <p>
                        {isMarathi() 
                          ? 'या अल्बममध्ये कोणतीही छायाचित्रे उपलब्ध नाहीत' 
                          : 'No photos available in this album'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Albums View */
              <div className="albums-container">
                <div className="albums-header">
                  <p className="albums-count">
                    {isMarathi() 
                      ? `${yearAlbums.length} अल्बम उपलब्ध` 
                      : `${yearAlbums.length} Albums Available`
                    }
                  </p>
                </div>

                <div className="albums-grid">
                  {yearAlbums.length > 0 ? (
                    yearAlbums.map((album) => {
                      const albumStats = getAlbumStats(selectedYear, album.id);
                      return (
                        <div
                          key={album.id}
                          className="album-card"
                          onClick={() => handleAlbumClick(album.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleAlbumClick(album.id);
                            }
                          }}
                        >
                          <div className="album-card-image">
                            {albumStats?.thumbnail && (
                              <img 
                                src={albumStats.thumbnail} 
                                alt={isMarathi() ? album.title.marathi : album.title.english}
                                className="album-thumbnail"
                              />
                            )}
                            <div className="album-card-overlay">
                              <h3 className="album-card-title">
                                {isMarathi() ? album.title.marathi : album.title.english}
                              </h3>
                            </div>
                          </div>
                          <div className="album-card-info">
                            <p className="album-card-stats">
                              {albumStats?.photoCount || 0} {isMarathi() ? 'छायाचित्रे' : 'Photos'}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="no-albums-message">
                      <p>
                        {isMarathi() 
                          ? 'या वर्षासाठी कोणतेही अल्बम उपलब्ध नाहीत' 
                          : 'No albums available for this year'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lightbox */}
        {!showingYearList && lightboxPhotos.length > 0 && (
          <Lightbox
            isOpen={isLightboxOpen}
            onClose={handleLightboxClose}
            photos={lightboxPhotos}
            currentIndex={currentPhotoIndex}
            onPrevious={handlePreviousPhoto}
            onNext={handleNextPhoto}
          />
        )}
      </div>
    </div>
  );
};

export default PhotoGallery;

